"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.pruneAmino = exports.generateAminoType = exports.paramConverter = exports.mapEachIndiv = exports.ConvertEncType = void 0;
const util_1 = require("../../util");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const long_1 = __importDefault(require("long"));
var ConvertEncType;
(function (ConvertEncType) {
    ConvertEncType["Long"] = "long";
    ConvertEncType["LongToNum"] = "long-number";
    ConvertEncType["Dec"] = "dec";
    ConvertEncType["DecOrZero"] = "dec-or-zero";
    ConvertEncType["NumToStr"] = "number-str";
    ConvertEncType["Date"] = "date";
    ConvertEncType["DateToNum"] = "date-number";
    ConvertEncType["Duration"] = "duration";
})(ConvertEncType = exports.ConvertEncType || (exports.ConvertEncType = {}));
;
/**
 * checks if value is Long, BigNumber, Date, Duration or Uint8Array
 * @param value value to check
 */
const typeCheck = (value) => {
    return long_1.default.isLong(value)
        || bignumber_js_1.default.isBigNumber(value)
        || value instanceof Uint8Array
        || value instanceof Date;
};
/**
 * checks maps object to amino or direct form
 * @param mapItem obj to be converted
 * @param valueKey obj with labels that indicate how each value is treated
 * @param toAmino indicates whether to convert to amino or direct
 */
const mapEachIndiv = (mapItem, valueKey, toAmino = false) => {
    var _a;
    if (!mapItem) {
        return mapItem;
    }
    const indivDirectKeys = (_a = Object.keys(mapItem)) !== null && _a !== void 0 ? _a : [];
    const directMap = {};
    indivDirectKeys.forEach((key) => {
        const altKey = toAmino ? util_1.TypeUtils.camelToSnake(key) : util_1.TypeUtils.snakeToCamel(key);
        const keyMap = toAmino ? valueKey === null || valueKey === void 0 ? void 0 : valueKey[key] : valueKey === null || valueKey === void 0 ? void 0 : valueKey[altKey];
        if (typeof keyMap !== "object") {
            // Check if this is a Long/BigNumber/Buffer/Date/Duration obj or a non-object/array
            if (typeCheck(mapItem[key]) || typeof mapItem[key] !== "object") {
                directMap[altKey] = (0, exports.paramConverter)(mapItem[key], keyMap, toAmino);
                return;
            }
        }
        if (mapItem[key].length && typeof mapItem[key] === "object") {
            // If value is an array of objects, iterate through objects and call mapToObj function
            directMap[altKey] = mapItem[key].map((newMap) => {
                return (0, exports.mapEachIndiv)(newMap, valueKey, toAmino);
            });
            return;
        }
        else {
            if (util_1.TypeUtils.isDurationType(mapItem[key])) {
                directMap[altKey] = (0, exports.paramConverter)(mapItem[key], keyMap, toAmino);
                return;
            }
            directMap[altKey] = (0, exports.mapEachIndiv)(mapItem[key], keyMap, toAmino);
        }
    });
    return directMap;
};
exports.mapEachIndiv = mapEachIndiv;
/**
 * convert direct params to corresponding amino params
 * @param value direct params to be converted
 * @param type param type (check ConvertEncType for list of types)
 * @param toAmino indicates whether to convert to amino or direct
 */
const paramConverter = (value, type, toAmino = false) => {
    var _a, _b, _c;
    if (!value) {
        return value;
    }
    switch (type) {
        case ConvertEncType.Dec:
            const bnVal = util_1.NumberUtils.bnOrZero(value);
            return toAmino
                ? bnVal.shiftedBy(-18).toFixed(18)
                : bnVal.shiftedBy(18).toString(10);
        case ConvertEncType.DecOrZero:
            const decBnVal = util_1.NumberUtils.bnOrZero(value);
            if (decBnVal.isZero())
                return "0";
            return toAmino
                ? decBnVal.shiftedBy(-18).toFixed(18)
                : decBnVal.shiftedBy(18).toString(10);
        case ConvertEncType.Long:
            return toAmino ? value.toString() : new long_1.default(Number(value));
        case ConvertEncType.LongToNum:
            return toAmino ? value.toNumber() : new long_1.default(value);
        case ConvertEncType.NumToStr:
            return toAmino ? value.toString() : Number(value);
        case ConvertEncType.Date:
            return toAmino ? value.toISOString().replace('.000', '') : new Date(value);
        case ConvertEncType.DateToNum:
            if (toAmino) {
                const timestampBN = new bignumber_js_1.default((_a = value.getTime()) !== null && _a !== void 0 ? _a : 0).shiftedBy(-3).decimalPlaces(0, 1);
                return timestampBN.toNumber();
            }
            else {
                const timestampNum = (value !== null && value !== void 0 ? value : 0) * 1000;
                return new Date(timestampNum);
            }
        case ConvertEncType.Duration:
            // Process Duration model from google protobuf
            if (toAmino) {
                const nanosBN = new bignumber_js_1.default((_b = value === null || value === void 0 ? void 0 : value.nanos) !== null && _b !== void 0 ? _b : 0).shiftedBy(-6);
                const seconds = (_c = value === null || value === void 0 ? void 0 : value.seconds) !== null && _c !== void 0 ? _c : new long_1.default(0);
                return `${nanosBN.plus(seconds.toString()).toString(10)}`;
            }
            else {
                const durationBN = util_1.NumberUtils.bnOrZero(value.replace('s', ''));
                const secondsBN = durationBN.decimalPlaces(0, 1);
                const nanosBN = durationBN.minus(secondsBN).shiftedBy(6).decimalPlaces(0, 1);
                return {
                    seconds: new long_1.default(secondsBN.toNumber()),
                    nanos: nanosBN.toNumber(),
                };
            }
        default:
            return value;
    }
};
exports.paramConverter = paramConverter;
const generateAminoType = (amino, aminoProcess = {}) => {
    var _a;
    const valueMap = (_a = amino.valueMap) !== null && _a !== void 0 ? _a : {};
    return Object.assign(Object.assign({}, amino), { toAmino: (input) => {
            const processRes = (aminoProcess === null || aminoProcess === void 0 ? void 0 : aminoProcess.toAminoProcess)
                ? aminoProcess.toAminoProcess(valueMap, input)
                : {};
            const { input: newInput = input, amino: newAminoMap = valueMap } = processRes;
            const aminoObj = {};
            Object.keys(newInput).forEach((key) => {
                var _a;
                const snakeKey = util_1.TypeUtils.camelToSnake(key);
                if (!newInput[key])
                    return;
                if (typeCheck(newInput[key])) {
                    aminoObj[snakeKey] = (0, exports.paramConverter)(newInput[key], newAminoMap[key], true);
                    return;
                }
                if (typeof newInput[key] !== "object" && typeof newAminoMap[key] !== "object") {
                    aminoObj[snakeKey] = (0, exports.paramConverter)(newInput[key], newAminoMap[key], true);
                }
                else {
                    if (((_a = newInput[key]) === null || _a === void 0 ? void 0 : _a.length) && typeof newInput[key] === "object") {
                        aminoObj[snakeKey] = newInput[key].map((newItem) => (0, exports.mapEachIndiv)(newItem, newAminoMap[key], true));
                        return;
                    }
                    aminoObj[snakeKey] = (0, exports.mapEachIndiv)(newInput[key], newAminoMap[key], true);
                }
            });
            return aminoObj;
        }, fromAmino: (input) => {
            const processRes = (aminoProcess === null || aminoProcess === void 0 ? void 0 : aminoProcess.fromAminoProcess)
                ? aminoProcess.fromAminoProcess(valueMap, input)
                : {};
            const { input: newInput = input, amino: newAminoMap = valueMap } = processRes;
            const aminoObj = {};
            Object.keys(newInput).forEach((key) => {
                var _a;
                const camelKey = util_1.TypeUtils.snakeToCamel(key);
                if (typeof newInput[key] !== "object" && typeof newAminoMap[key] !== "object") {
                    aminoObj[camelKey] = (0, exports.paramConverter)(newInput[key], newAminoMap[camelKey], false);
                }
                else {
                    if (((_a = newInput[key]) === null || _a === void 0 ? void 0 : _a.length) && typeof newInput[key] === "object") {
                        aminoObj[camelKey] = newInput[key].map((newItem) => (0, exports.mapEachIndiv)(newItem, newAminoMap[camelKey], false));
                        return;
                    }
                    aminoObj[camelKey] = (0, exports.mapEachIndiv)(newInput[key], newAminoMap[camelKey], false);
                }
            });
            return aminoObj;
        } });
};
exports.generateAminoType = generateAminoType;
const pruneAmino = (value, pruneMap) => {
    const newValue = value;
    const newMsg = value.msg;
    Object.entries(newMsg).forEach(([key, value]) => {
        const camelKey = util_1.TypeUtils.snakeToCamel(key);
        const pruneItem = pruneMap[camelKey];
        switch (pruneItem) {
            case ConvertEncType.Long:
                if (long_1.default.isLong(value) && value.isZero()) {
                    delete newMsg[key];
                }
            default:
                if (typeof value === "boolean" && !value) {
                    delete newMsg[key];
                }
                if (typeof value === "string" && value === "") {
                    delete newMsg[key];
                }
                if (typeof value === "number" && value === 0) {
                    delete newMsg[key];
                }
        }
    });
    newValue.msg = newMsg;
    return newValue;
};
exports.pruneAmino = pruneAmino;
